/*****************************************************************
 *  CUSTOMER BUTLER - THE THIN-LINE ICONS                          *
 *  File: apps/web/src/pages/customer/customerIcons.js             *
 *  What it does: draws the few icons the customer pages use, as   *
 *  React SVGs, in the suite's ONE icon family (family A: thin     *
 *  line, 24x24 box, round caps; size and stroke come from the     *
 *  .bc-icon rule in butler-tokens.css).                           *
 *  The shapes are the same ones server/src/shared/design/         *
 *  butler-icons.js draws (that file paints plain HTML pages and   *
 *  cannot be imported into React). New shapes here follow the     *
 *  same grid.                                                     *
 *  Reads: nothing.  Writes: nothing.                              *
 *****************************************************************/

import React from 'react';

/*****************************************************************
 *  SECTION 1 - THE SHAPES (by name)                               *
 *****************************************************************/

const ICON_SHAPES = {
  search: ['M21 21l-4.3-4.3', 'CIRCLE:11:11:7'],
  plus: ['M12 5v14M5 12h14'],
  check: ['M20 6 9 17l-5-5'],
  warn: ['M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z', 'M12 9v4M12 17h.01'],
  close: ['M18 6 6 18M6 6l12 12'],
  pencil: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z'],
  chevron: ['m9 18 6-6-6-6'],
  back: ['m15 18-6-6 6-6'],
  people: ['M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'CIRCLE:9.5:7:4', 'M22 21v-2a4 4 0 0 0-3-3.9', 'M16 3.1a4 4 0 0 1 0 7.8'],
  home: ['M3 10.5 12 3l9 7.5', 'M5 9.5V21h14V9.5'],
  file: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5'],
  clock: ['CIRCLE:12:12:9', 'M12 7v5l3.5 2'],
  link: ['M10 13a5 5 0 0 0 7.5.6l3-3a5 5 0 0 0-7-7l-1.7 1.7', 'M14 11a5 5 0 0 0-7.5-.6l-3 3a5 5 0 0 0 7 7l1.7-1.7'],
  archive: ['M2 4h20v5H2z', 'M4 9v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9M10 13h4'],
  offline: ['M2 2l20 20', 'M8.5 16.5a5 5 0 0 1 7 0', 'M5 12.6a10 10 0 0 1 5.2-2.8', 'M12 20h.01'],
  // lock (2026-10-14): the "no access" pages on Customer Butler.
  lock: ['M5 11h14v10H5z', 'M8 11V7a4 4 0 0 1 8 0v4'],
  gear: ['CIRCLE:12:12:3', 'M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1 7 17M17 7l2.1-2.1'],
  // arrowleft, arrowright, user, briefcase, tag, shield, help (2026-10-15):
  // the HR Butler chrome - "<- Hub", "<- Back to customers", the folded
  // cards' right arrow, the record chips. Same shapes as butler-icons.js.
  arrowleft: ['M19 12H5M12 19l-7-7 7-7'],
  arrowright: ['M5 12h14M12 5l7 7-7 7'],
  user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'CIRCLE:12:7:4'],
  briefcase: ['M2 7h20v14H2z', 'M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16'],
  tag: ['M20.6 13.4 12 22l-9-9V3h10l7.6 7.6a2 2 0 0 1 0 2.8z', 'M7.5 7.5h.01'],
  shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
  help: ['CIRCLE:12:12:9.25', 'M9.2 9.3a2.9 2.9 0 0 1 5.6 1c0 1.9-2.8 2.5-2.8 4', 'M12 17.4h.01'],
  // truck, phone, drop, calendar (2026-10-14): the source chips on the
  // Activities tab's "From the other Butlers" list (Dispatch, On-Call,
  // Water test, Schedule). Same 24x24 thin-line grid.
  truck: ['M1 6h13v10H1z', 'M14 9h4l4 4v3h-8', 'CIRCLE:5.5:18:2', 'CIRCLE:18.5:18:2'],
  phone: ['M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z'],
  drop: ['M12 2.7 6.3 8.4a8 8 0 1 0 11.4 0z'],
  calendar: ['M3 5h18v16H3z', 'M16 3v4M8 3v4M3 10h18'],
  // undo, ban (2026-10-15, BACKLOG 317): "Report send pulled back for
  // correction" on the timeline (an arrow turning back), and "Events the
  // door refused" in CRM Setup (a circle with a stroke through it).
  // Same 24x24 thin-line grid.
  undo: ['M9 14 4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11'],
  ban: ['CIRCLE:12:12:9', 'M5.6 5.6l12.8 12.8'],
};

/*****************************************************************
 *  SECTION 2 - DRAWING ONE ICON                                   *
 *****************************************************************/

/* ---- drawPiece(piece, pieceIndex) ------------------------------
   One part of a shape: a path, or "CIRCLE:cx:cy:r".             */
function drawPiece(piece, pieceIndex) {
  const isCircle = piece.startsWith('CIRCLE:');

  if (isCircle) {
    const circleParts = piece.split(':');

    return <circle key={pieceIndex} cx={circleParts[1]} cy={circleParts[2]} r={circleParts[3]} />;
  }

  return <path key={pieceIndex} d={piece} />;
}

/* ---- Icon({ name, size }) --------------------------------------
   In: an icon name from section 1; size "inline" (18px) or
   "button" (22px). Out: an SVG the screen reader skips (every
   icon on these pages sits beside a word that says the meaning). */
export function Icon({ name, size }) {
  const shapePieces = ICON_SHAPES[name] || ICON_SHAPES.file;
  const sizeClass = size === 'button' ? 'bc-icon bc-icon-btn' : 'bc-icon';

  return (
    <svg className={sizeClass} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {shapePieces.map(drawPiece)}
    </svg>
  );
}

export default Icon;
